import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import { 
  FiHome, 
  FiUsers, 
  FiUpload, 
  FiBell, 
  FiDollarSign, 
  FiBarChart2, 
  FiLogOut,
  FiShield,
  FiUser,
  FiMenu,
  FiX,
  FiCheckSquare,
  FiDownload,
  FiSmartphone,
  FiXCircle,
  FiAlertCircle
} from 'react-icons/fi';

const Layout = () => {
  const { user, logout, isOwner, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [appInstalled, setAppInstalled] = useState(false);
  const [installError, setInstallError] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    // Verificar si la app ya está instalada
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInstalled = localStorage.getItem('appInstalled') === 'true';
    setAppInstalled(isStandalone || isInstalled);

    const handleBeforeInstallPrompt = (e) => {
      console.log('✅ Evento beforeinstallprompt capturado');
      e.preventDefault();
      setDeferredPrompt(e);
      
      if ((isOwner || isAdmin) && !(isStandalone || isInstalled)) {
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      console.log('✅ App instalada exitosamente');
      setAppInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem('appInstalled', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isOwner, isAdmin]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('❌ No hay evento de instalación disponible');
      setInstallError(true);
      setTimeout(() => setInstallError(false), 3000);
      return;
    }

    try {
      console.log('🔄 Mostrando prompt de instalación...');
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      console.log('📝 Resultado:', outcome);
      
      if (outcome === 'accepted') {
        console.log('✅ Usuario aceptó instalar');
        setAppInstalled(true);
        setShowInstallPrompt(false);
        localStorage.setItem('appInstalled', 'true');
      } else {
        console.log('❌ Usuario canceló la instalación');
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('❌ Error en instalación:', error);
      setInstallError(true);
      setTimeout(() => setInstallError(false), 3000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const canSeeTasks = isOwner || isAdmin;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header Superior con Iconos */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-30 h-16 flex items-center px-4">
        {/* Botón Hamburguesa (solo móvil) */}
        <button
          onClick={toggleSidebar}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        {/* Logo */}
        <h1 className="text-lg font-bold text-blue-600 ml-2 md:ml-0">Flex Energy</h1>

        {/* Espaciador */}
        <div className="flex-1"></div>

        {/* Iconos de la derecha */}
        <div className="flex items-center space-x-3">
          {/* Campana de notificaciones */}
          <NotificationBell />
          
          {/* Perfil */}
          <Link
            to="/profile"
            className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
            title="Mi Perfil"
          >
            <FiUser className="text-blue-600" size={20} />
          </Link>
        </div>
      </div>

      {/* Overlay móvil */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${
          isMobile
            ? `fixed inset-y-0 left-0 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out z-50`
            : 'fixed inset-y-0 left-0'
        } w-64 bg-white shadow-lg mt-16`}
      >
        <div className="flex flex-col h-full">
          {/* User Info */}
          <div className="p-4 border-b">
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-full">
                <FiUser className="text-blue-600" size={20} />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Usuario'}</p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'owner' ? 'Dueño' : 
                   user?.role === 'admin' ? `Admin ${user?.adminId}` : 'Usuario'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 overflow-y-auto">
            <Link
              to="/dashboard"
              onClick={closeSidebar}
              className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
            >
              <FiHome className="mr-3" size={18} />
              Dashboard
            </Link>

            <Link
              to="/clients"
              onClick={closeSidebar}
              className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
            >
              <FiUsers className="mr-3" size={18} />
              Clientes
            </Link>

            <Link
              to="/clients/import"
              onClick={closeSidebar}
              className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
            >
              <FiUpload className="mr-3" size={18} />
              Importar Clientes
            </Link>

            {canSeeTasks && (
              <Link
                to="/tasks"
                onClick={closeSidebar}
                className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
              >
                <FiCheckSquare className="mr-3" size={18} />
                Tareas
              </Link>
            )}

            <Link
              to="/notifications"
              onClick={closeSidebar}
              className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
            >
              <FiBell className="mr-3" size={18} />
              Notificaciones
            </Link>

            <Link
              to="/reports"
              onClick={closeSidebar}
              className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
            >
              <FiBarChart2 className="mr-3" size={18} />
              Reportes
            </Link>

            {isOwner && (
              <Link
                to="/users"
                onClick={closeSidebar}
                className="flex items-center px-4 py-3 text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 mb-1 transition-colors"
              >
                <FiShield className="mr-3" size={18} />
                Usuarios
              </Link>
            )}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiLogOut className="mr-2" size={18} />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${isMobile ? 'pt-16' : 'pt-16 ml-64'}`}>
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mensaje de error si la instalación falla */}
      {installError && (
        <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg z-50 shadow-lg">
          <div className="flex items-center">
            <FiAlertCircle className="mr-2 flex-shrink-0" size={18} />
            <span className="text-sm">
              No se pudo iniciar la instalación. Tu navegador puede no ser compatible.
            </span>
          </div>
        </div>
      )}

      {/* Botón de instalación SOLO UNO - con mejor posicionamiento */}
      {(isOwner || isAdmin) && !appInstalled && (
        <>
          {showInstallPrompt && deferredPrompt && (
            <div className="fixed bottom-20 right-4 left-4 md:left-auto md:w-96 bg-white rounded-lg shadow-2xl border border-blue-200 z-50 animate-slide-up">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
                <div className="flex items-center">
                  <FiSmartphone className="mr-2" size={18} />
                  <span className="font-semibold">📱 Instalar App Flex Energy</span>
                </div>
                <button 
                  onClick={() => setShowInstallPrompt(false)}
                  className="text-white hover:text-blue-200"
                >
                  <FiXCircle size={18} />
                </button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Instala la app en tu dispositivo para acceder más rápido y recibir notificaciones.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleInstall}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center text-sm font-medium"
                  >
                    <FiDownload className="mr-2" size={16} />
                    Instalar ahora
                  </button>
                  <button
                    onClick={() => setShowInstallPrompt(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-600"
                  >
                    Ahora no
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Botón flotante único - reposicionado para no tapar el chat */}
          {!showInstallPrompt && (
            <button
              onClick={() => setShowInstallPrompt(true)}
              className="fixed bottom-20 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all z-40 animate-pulse"
              title="Instalar app"
            >
              <FiDownload size={24} />
            </button>
          )}
        </>
      )}

      {/* CSS para animación */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Layout;